'use client';

import { VDPChargingBatteryCapacityCard } from '@/components/organisms/VDPChargingBatteryCapacityCard/VDPChargingBatteryCapacityCard';
import { VDPChargingDrivingRangeCard } from '@/components/organisms/VDPChargingDrivingRangeCard/VDPChargingDrivingRangeCard';
import { VDPChargingTimeCard } from '@/components/organisms/VDPChargingTimeCard/VDPChargingTimeCard';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { TrimSpecs } from '@/lib/schema/build/types';
import { BATTERY_TYPES } from '@/lib/constants';
import { getBatteryWarrantyRating, getVehiclePower } from '@/lib/report';
import { RangeMapLegend } from '@/components/molecules/RangeMap';
import { ValueWithUnit } from '@/types';
import { NotFoundIframeContent } from '@/app/iframes/components/NotFoundIframeContent';
import { AuthProvider } from '@/components/molecules/AuthProvider';
import {
  BatteryInfoType,
  ChargingInfoType,
  RawVehicleType,
} from '../../../lib/schema/inventory/types';
import { Range } from '../../../lib/schema/shared/types';
import { FuelTypes } from '../../../types/enums';
import { PoweredByFooter } from '../../molecules/PoweredByFooter';
import { BatteryOverlay, ChargingOverlay } from './Overlays';

// Dynamically import RangeMap to avoid SSR issues with Leaflet
const RangeMap = dynamic(
  () =>
    import('@/components/molecules/RangeMap/RangeMap').then((mod) => ({
      default: mod.RangeMap,
    })),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-[200px] w-full items-center justify-center ml:h-[276px]">
        <div className="text-center">
          <div>Loading Range Map...</div>
          <div className="mt-[8px] text-sm text-gray-500">Initializing map</div>
        </div>
      </div>
    ),
  }
);

export interface ChargingIframeProps {
  product: {
    chargingInfo: ChargingInfoType;
    batteryWarranty: string;
    fuelType: FuelTypes;
    batteryInfo: BatteryInfoType;
    rangeInfo: Range;
    year: number;
    make: string;
    model: string;
    trim: string;
    mileage: ValueWithUnit;
    trimSpecs: TrimSpecs | null;
  } | null;
  showFooter?: boolean;
  rangeInfo?: Range | null;
  vehicleListing?: RawVehicleType | null;
  selectedZipCode?: string;
}

const ChargingIframeComponent: React.FC<ChargingIframeProps> = ({
  product,
  showFooter = true,
  rangeInfo,
  vehicleListing,
  selectedZipCode,
}) => {
  const searchParams = useSearchParams();
  const showMap = searchParams?.get('showMap');

  // Send iframe height to parent window
  useEffect(() => {
    function sendIframeHeight() {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage(
        {
          type: 'setIframeHeight',
          height,
        },
        '*' // Or replace with specific domain for security
      );
    }

    sendIframeHeight(); // Send on load

    window.addEventListener('resize', sendIframeHeight);

    return () => {
      window.removeEventListener('resize', sendIframeHeight);
    };
  }, []);

  const { warrantyString, warrantyActive } = getBatteryWarrantyRating({
    batteryWarranty: product?.trimSpecs?.batteryWarranty,
    mileage: product?.mileage,
    year: product?.year,
  });

  // battery tech
  const batteryType = product?.batteryInfo.batteryTech
    .toLowerCase()
    .replace(/-/g, ' ')
    .trim();
  let batteryTypeCard = null;
  if (batteryType?.includes('lithium ion') || batteryType?.includes('li ion')) {
    batteryTypeCard = BATTERY_TYPES.lithiumIon;
  } else if (
    batteryType?.includes('lfp') ||
    batteryType?.includes('lithium iron phosphate')
  ) {
    batteryTypeCard = BATTERY_TYPES.lfp;
  } else if (batteryType?.includes('lithium polymer')) {
    batteryTypeCard = BATTERY_TYPES.lithiumPolymer;
  } else if (
    batteryType?.includes('nmc') ||
    batteryType?.includes('nickel manganese cobalt') ||
    batteryType?.includes('nickel cobalt manganese')
  ) {
    batteryTypeCard = BATTERY_TYPES.nmc;
  } else if (
    batteryType?.includes('nimh') ||
    batteryType?.includes('nickel metal hydride')
  ) {
    batteryTypeCard = BATTERY_TYPES.nimh;
  } else if (batteryType?.includes('solid state')) {
    batteryTypeCard = BATTERY_TYPES.solidState;
  } else if (batteryType?.includes('lead acid')) {
    batteryTypeCard = BATTERY_TYPES.leadAcid;
  }
  const batterySize = product?.batteryInfo.batterySize
    ? product.batteryInfo.batterySize
    : 'N/A';

  const fuelEconomySpecs = product?.trimSpecs?.moreDetails[
    'Fuel Economy and Emissions'
  ]
    .find((detail) => detail.id === '17311')
    ?.value.replace('mpg', 'MPG');

  const fuelEconomyEstimate =
    product?.rangeInfo.value && product?.batteryInfo.batterySize
      ? `${((33.7 * Number(product?.rangeInfo.value)) / Number(product?.batteryInfo.batterySize?.split(' ')[0])).toLocaleString()} MPGe`
      : null;

  const fuelEconomy = fuelEconomySpecs || fuelEconomyEstimate;
  const horsepower = product?.trimSpecs?.highlights.Horsepower || null;
  const ePower = getVehiclePower({ trimSpecs: product?.trimSpecs || null });

  return (
    <div className="min-h-iframe flex flex-col overflow-hidden rounded-small bg-white">
      <>
        {/* header */}
        <div className="flex flex-wrap items-center justify-between bg-brandMidnight px-m py-s text-white m:px-l">
          <div className="text-h4SemiBold">Charging</div>
          {product && (
            <div className="overflow-hidden text-ellipsis whitespace-nowrap text-body2Regular">
              {product.year} {product.make} {product.model} {product.trim}
            </div>
          )}
        </div>

        {!product && <NotFoundIframeContent type="charging" />}

        {/* main */}
        {product && (
          <div className="flex flex-grow flex-col">
            <div className="flex flex-grow flex-col items-stretch gap-s px-m py-l iframe-row-collapse:flex-row iframe-row-collapse:gap-xl iframe-row-collapse:p-l">
              <div className="flex flex-1">
                <VDPChargingTimeCard
                  chargingInfo={product.chargingInfo}
                  fueltype={product.fuelType}
                />
              </div>
              <div className="flex flex-1">
                <VDPChargingBatteryCapacityCard
                  capacity={
                    Number(
                      product?.batteryInfo?.batterySize?.trim().split(' ')[0]
                    ) || null
                  }
                  fuelType={product.fuelType}
                />
              </div>
              <div className="flex flex-1">
                <VDPChargingDrivingRangeCard
                  rangeInfo={product.rangeInfo}
                  fuelType={product.fuelType}
                />
              </div>
            </div>
            <div className="flex h-full w-full flex-col px-m">
              {showMap && (
                <>
                  <div className="h-px w-full bg-neutral-200" />
                  {rangeInfo ? (
                    <div className="my-l">
                      <div className="h-[400px] w-full">
                        <RangeMap
                          range={rangeInfo}
                          region={'US'}
                          zipcode={selectedZipCode}
                          dealerZip={vehicleListing?.listing?.Postal}
                          disableAuthCheck={true}
                        />
                      </div>
                      <div className="mt-l">
                        <RangeMapLegend />
                      </div>
                    </div>
                  ) : (
                    <div className="py-2xl text-center">
                      <p>No range data available for this vehicle.</p>
                    </div>
                  )}
                </>
              )}
              <div className="h-px w-full bg-neutral-200" />

              <ChargingOverlay
                chargingInfo={product.chargingInfo}
                trimSpecs={product.trimSpecs || null}
              />
              <div className="mt-m h-px w-full bg-neutral-200" />
              <BatteryOverlay
                batterySize={batterySize}
                fuelEconomy={fuelEconomy}
                batteryTypeCard={batteryTypeCard}
                horsepower={horsepower}
                ePower={ePower}
                warrantyString={warrantyString}
                warrantyActive={warrantyActive}
              />
            </div>
          </div>
        )}
      </>

      {showFooter && <PoweredByFooter bgFill={true} />}
    </div>
  );
};

const ChargingIframe = (props: ChargingIframeProps) => (
  <Suspense>
    <AuthProvider>
      <ChargingIframeComponent {...props} />
    </AuthProvider>
  </Suspense>
);

export default ChargingIframe;
